import * as React from "react";

import type { EmailChangeActionResponse } from "@/api/email-change";
import { Button } from "@/components/ui/button";
import { takeEmailChangeActionToken } from "@/views/auth/email-change/emailChangeFragment";

type ActionState = "processing" | "confirmed" | "reverted" | "retry" | "invalid" | "throttled" | "support";

interface Props {
  action: "confirm" | "revert";
  submit: (payload: { token: string }) => Promise<EmailChangeActionResponse>;
  isAuthenticated?: boolean;
}

interface ActionContent {
  heading: string;
  message: string;
  link?: {
    href: string;
    label: string;
  };
}

const linkClassName =
  "font-medium text-primary underline-offset-4 hover:text-primary/80 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const emailChangeActionErrorState = (error: unknown): ActionState => {
  const status =
    typeof error === "object" && error !== null && "status" in error
      ? Number((error as { status?: unknown }).status)
      : undefined;
  if (status === 429) return "throttled";
  if (status === 409) return "support";
  if (status === 503) return "retry";
  return "invalid";
};

const actionContent = (state: ActionState, action: Props["action"], isAuthenticated: boolean): ActionContent => {
  if (state === "processing") {
    return action === "confirm"
      ? {
          heading: "Confirming your email address",
          message: "Keep this page open while we complete your email change.",
        }
      : {
          heading: "Securing your account",
          message: "Keep this page open while we revert the email change.",
        };
  }

  if (state === "confirmed") {
    return {
      heading: "Email address confirmed",
      message: "Your new email address is now active on your Pathfinder account.",
      link: isAuthenticated ? { href: "/", label: "Continue to Pathfinder" } : { href: "/login", label: "Sign in" },
    };
  }

  if (state === "reverted") {
    return {
      heading: "Email change reverted",
      message:
        "Your account is secured. Every session has been signed out. Your former password no longer works. Reset your password before signing in again.",
      link: { href: "/reset-password", label: "Reset your password" },
    };
  }

  if (state === "throttled") {
    return {
      heading: "Too many attempts",
      message:
        "Wait before opening the original email link again. No account details have been changed by this attempt.",
    };
  }

  if (state === "support") {
    return {
      heading: "We couldn't complete this email change",
      message:
        "Open the original email link again to retry. If the change still cannot be completed, contact your organization administrator or support team.",
    };
  }

  if (state === "retry") {
    return {
      heading: "Email change temporarily unavailable",
      message: "No account details were changed. Try the secure request again when you are ready.",
    };
  }

  return {
    heading: "Email change link unavailable",
    message:
      "This link is invalid, expired, or has already been used. Start a new email change from your account or contact your organization administrator.",
  };
};

export const EmailChangeActionView = ({ action, submit, isAuthenticated = false }: Props) => {
  const submitted = React.useRef(false);
  const actionToken = React.useRef<string | undefined>(undefined);
  const requestInFlight = React.useRef(false);
  const [state, setState] = React.useState<ActionState>("processing");

  const submitToken = React.useCallback(
    async (token: string) => {
      if (requestInFlight.current) return;
      requestInFlight.current = true;
      setState("processing");
      try {
        const result = await submit({ token });
        actionToken.current = undefined;
        setState(result.status);
      } catch (error) {
        const errorState = emailChangeActionErrorState(error);
        if (errorState !== "retry") actionToken.current = undefined;
        setState(errorState);
      } finally {
        requestInFlight.current = false;
      }
    },
    [submit],
  );

  React.useEffect(() => {
    if (submitted.current) return;
    submitted.current = true;
    const token = takeEmailChangeActionToken() ?? window.location.hash.slice(1);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (!token) {
      setState("invalid");
      return;
    }
    actionToken.current = token;
    void submitToken(token);
  }, [submitToken]);

  const handleRetry = () => {
    const token = actionToken.current;
    if (!token) {
      setState("invalid");
      return;
    }
    void submitToken(token);
  };

  const content = actionContent(state, action, isAuthenticated);

  return (
    <section
      role="status"
      aria-live="polite"
      aria-busy={state === "processing"}
      className="space-y-5 text-center text-ui-paragraph-text"
    >
      <h1 className="text-2xl font-semibold leading-tight tracking-tight">{content.heading}</h1>
      <p className="text-sm leading-6">{content.message}</p>
      {state === "retry" ? (
        <Button type="button" onClick={handleRetry}>
          Try again
        </Button>
      ) : content.link ? (
        <p className="pt-1 text-sm">
          <a className={linkClassName} href={content.link.href}>
            {content.link.label}
          </a>
        </p>
      ) : null}
    </section>
  );
};
